// Session-wipe helper that does not destroy third-party analytics state.
//
// posthog-js persists distinct_id, device_id, session_id, $user_state and its
// not-yet-flushed event queue in localStorage under keys prefixed `ph_`,
// `__ph_opt_in_out_` and `__posthog`. `localStorage.clear()` removes EVERY key
// for the origin, so the session-expiry / logout handlers spread across this app
// were silently:
//   - destroying the PostHog identity (later events land on a new anonymous person)
//   - resetting session_id, which cuts the session replay into an orphaned session
//   - deleting queued events that had not been transmitted yet
//
// Use this instead of localStorage.clear() anywhere the app wipes a session.
// It removes exactly the same app keys; it just leaves analytics state alone.

// `__felog*` is the frontend activity log's own buffer (see common/frontendLog).
// It is preserved for the same reason as the analytics keys, only more so: the
// session-expiry handler wipes storage and hard-redirects, and the entries
// explaining WHY the session ended are written moments before that wipe. Clearing
// them here would delete the log of the failure at exactly the moment it happens.
const PRESERVED_KEY = /^(ph_|__ph|__posthog|__felog)/;

// Safe as a static import: frontendLog imports nothing from this module, so there
// is no cycle. It is the log's in-memory copy of the identity, not its buffer —
// the buffer is preserved above, the identity must not be.
import { clearLogUser } from './frontendLog';

export const clearAppStorage = (): void => {
  // The buffered ENTRIES survive this wipe (see PRESERVED_KEY) but the identity
  // must not: it is module state, so it would otherwise outlive the session and
  // attribute the next signed-out action to whoever just left.
  clearLogUser();
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !PRESERVED_KEY.test(key)) doomed.push(key);
    }
    // Collect first, then delete — removing while iterating by index shifts the
    // remaining keys and would skip half of them.
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage access throws in private-mode / cookies-disabled browsers. A failed
    // wipe must never break the logout flow it is part of.
  }
};

export default clearAppStorage;
